import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { NewAgentModal } from "../components/NewAgentModal.tsx";
import {
	api,
	type AgentDirectoryEntry,
	type AgentInstance,
	type AgentStatusForLabel,
} from "../lib/api.ts";

/**
 * Global Agents directory — every project-agent we know about, grouped by the
 * business it serves, with a hierarchy (parent → child) and live status per
 * instance. Status comes from /api/agents/status (10s poll) so the page
 * doubles as a fleet dashboard.
 */
export function Agents() {
	const [agents, setAgents] = useState<AgentDirectoryEntry[] | null>(null);
	const [statuses, setStatuses] = useState<Map<string, AgentInstance[]>>(new Map());
	const [error, setError] = useState<string | null>(null);
	const [showNew, setShowNew] = useState(false);

	useEffect(() => {
		api
			.listAgents()
			.then((r) => setAgents(r.agents))
			.catch((err) => setError((err as Error).message));
	}, []);

	useEffect(() => {
		let cancelled = false;
		async function tick() {
			try {
				const r = await api.listAllAgentStatuses();
				if (cancelled) return;
				setStatuses(new Map(r.statuses.map((s: AgentStatusForLabel) => [s.label, s.instances])));
			} catch {
				/* keep prior map */
			}
		}
		tick();
		const id = setInterval(tick, 10_000);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, []);

	const grouped = useMemo(() => groupByBusiness(agents ?? []), [agents]);

	const onlineCount = useMemo(() => {
		let n = 0;
		for (const list of statuses.values()) n += list.filter((i) => i.online).length;
		return n;
	}, [statuses]);

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Agents</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{agents === null
							? "Loading…"
							: `${agents.length} agents across ${grouped.length} businesses · ${onlineCount} online`}
					</p>
				</div>
				<button
					type="button"
					onClick={() => setShowNew(true)}
					className="text-sm px-3 py-1.5 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-200 hover:bg-zinc-800/40"
				>
					+ New agent
				</button>
			</div>

			{showNew && <NewAgentModal onClose={() => setShowNew(false)} />}

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{agents !== null && agents.length === 0 && (
				<div className="text-sm text-zinc-500 italic">
					No dedicated project-agents yet — manager handles all businesses directly.
				</div>
			)}

			<div className="space-y-6">
				{grouped.map((group) => (
					<BusinessGroup key={group.businessId} group={group} statuses={statuses} />
				))}
			</div>
		</div>
	);
}

interface AgentNode {
	agent: AgentDirectoryEntry;
	children: AgentNode[];
}

interface BusinessGroupData {
	businessId: string;
	businessName: string;
	roots: AgentNode[];
	totalAgents: number;
}

function groupByBusiness(agents: AgentDirectoryEntry[]): BusinessGroupData[] {
	const byBusiness = new Map<string, AgentDirectoryEntry[]>();
	const nameByBusiness = new Map<string, string>();
	for (const a of agents) {
		nameByBusiness.set(a.businessId, a.businessName);
		const list = byBusiness.get(a.businessId);
		if (list) list.push(a);
		else byBusiness.set(a.businessId, [a]);
	}

	const groups: BusinessGroupData[] = [];
	for (const [businessId, list] of byBusiness) {
		const byKey = new Map<string, AgentNode>();
		for (const a of list) byKey.set(a.key, { agent: a, children: [] });
		const roots: AgentNode[] = [];
		for (const node of byKey.values()) {
			if (node.agent.parent && byKey.has(node.agent.parent)) {
				byKey.get(node.agent.parent)!.children.push(node);
			} else {
				roots.push(node);
			}
		}
		groups.push({
			businessId,
			businessName: nameByBusiness.get(businessId) ?? businessId,
			roots,
			totalAgents: list.length,
		});
	}
	groups.sort((a, b) => a.businessName.localeCompare(b.businessName));
	return groups;
}

function BusinessGroup({
	group,
	statuses,
}: {
	group: BusinessGroupData;
	statuses: Map<string, AgentInstance[]>;
}) {
	return (
		<section className="border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
				<div>
					<Link
						to={`/businesses/${group.businessId}`}
						className="text-base font-semibold text-zinc-100 hover:text-zinc-300"
					>
						{group.businessName}
					</Link>
					<span className="ml-2 text-xs text-zinc-500 font-mono">{group.businessId}</span>
				</div>
				<div className="text-xs text-zinc-500">
					{group.totalAgents} {group.totalAgents === 1 ? "agent" : "agents"}
				</div>
			</header>
			<div className="p-2">
				{group.roots.map((root) => (
					<AgentRow key={root.agent.key} node={root} statuses={statuses} depth={0} />
				))}
			</div>
		</section>
	);
}

function AgentRow({
	node,
	statuses,
	depth,
}: {
	node: AgentNode;
	statuses: Map<string, AgentInstance[]>;
	depth: number;
}) {
	const instances = statuses.get(node.agent.key) ?? [];
	const online = instances.filter((i) => i.online);
	const isOnline = online.length > 0;

	return (
		<>
			<Link
				to={`/agents/${node.agent.key}`}
				className="px-2 py-2 rounded hover:bg-zinc-800/40 flex items-center gap-3 transition-colors"
				style={{ marginLeft: depth * 24 }}
			>
				{depth > 0 && <span className="text-zinc-700 text-xs">└─</span>}
				<StatusDot online={isOnline} />
				<div className="min-w-0 flex-1">
					<div className="flex items-baseline gap-2 flex-wrap">
						<span className="text-sm font-medium text-zinc-100">{node.agent.displayName}</span>
						<span className="text-[11px] text-zinc-500 font-mono">{node.agent.key}</span>
						{node.agent.versions && (
							<span
								className="text-[10px] font-mono px-1 py-0.5 rounded bg-emerald-900/40 text-emerald-300"
								title="agent version = hash(prompt.md + AGENT.md + agent.json)"
							>
								@{node.agent.versions.agent}
							</span>
						)}
					</div>
					<div className="text-[11px] text-zinc-500 font-mono mt-0.5 truncate">
						tasks project: {node.agent.tasksProjectId}
					</div>
					{isOnline && (
						<div className="text-[11px] text-emerald-400/80 mt-0.5">
							{online.length === 1
								? `Running on ${online[0]!.hostname} · v${online[0]!.version}`
								: `Running on ${online.length} hosts`}
						</div>
					)}
				</div>
				<span className="text-zinc-600 text-sm">→</span>
			</Link>
			{node.children.map((child) => (
				<AgentRow key={child.agent.key} node={child} statuses={statuses} depth={depth + 1} />
			))}
		</>
	);
}

function StatusDot({ online }: { online: boolean }) {
	const cls = online
		? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]"
		: "bg-zinc-600";
	return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} aria-hidden />;
}
